/**
 * 產品憑證設計（`ProductSpec`）的共用取值helper（批93 §2.1 F）。
 *
 * 批93 之前，「有哪些模組／配額／套裝、預設期限是幾天」是前端寫死的常數
 * （`MODULE_LABELS`／`QUOTA_LABELS`／`PLAN_LABELS`）。多產品化之後這些全部改由後端
 * `GET /api/admin/products` 的憑證設計（spec）提供，本檔是**唯一**的解讀入口：
 * 任何「這個產品的預設期限是幾天／有哪些可販售套裝／全模組長什麼樣」都走這裡，
 * 不要在 view 裡各自 `spec.options.xxx`（缺鍵時 undefined 會靜默變成空欄位）。
 */
import type { ModuleSpec, PlanSpec, ProductSpec } from '@/api/ls'
import { MINUTES_PER_DAY, formatTerm, splitMinutes } from '@/lib/terms'

/**
 * 產品選項缺鍵時的兜底值——與後端 `services/products.OPTION_FALLBACKS` **逐鍵相同**。
 *
 * 後端 `spec_out()` 已經把兜底值併進回應了，所以正常情況下這份用不到；它存在的理由是
 * 「表單在 spec 還沒載回來（或載失敗）時仍要有合理初值」，以及讓 TypeScript 有型別可推。
 * ⚠️ 改這裡要同步改後端那一份。
 */
export const OPTION_FALLBACKS = {
  /**
   * @deprecated 批114 裁示 ㉖：**後台不再編輯、前端也不再用它決定任何顯示**。值改由
   * 配額鍵的宣告推導（後端 `services/quota_compat.derived_product_options`：那個鍵的
   * 中文名）。鍵仍在對外 `options` 輸出裡，所以兜底值留著；要顯示某一項配額的名稱，
   * 一律走 `lib/quotas.ts::quotaLabel`。
   */
  seat_label: '座席',
  grace_days: 14,
  default_term_days: 365,
  default_max_deployments: 1,
  term_day_options: [30, 365] as number[],
  /**
   * 批108：可選期限清單，每項是**總分鐘**（`term_days * 1440 + term_minutes`）。
   * 空陣列＝這個產品還沒設定分鐘級選項 → `termMinuteOptions()` 退回 `term_day_options`
   * 換算，既有產品零改動。
   */
  term_options: [] as number[],
  /**
   * @deprecated 同 `seat_label`（批114 裁示 ㉖）：值＝「該產品有沒有宣告那個配額鍵」，
   * 由後端推導。要判斷某一項配額存不存在，一律看 `spec.quotas` 有沒有那個鍵。
   */
  supports_max_product_lines: true,
  supports_deployment_mode: true,
  hosted_trial_term_days: 30,
  /**
   * 批108：這個產品按不按人頭賣。
   * @deprecated 同 `seat_label`（批114 裁示 ㉖）——由配額鍵的宣告推導，前端不再讀。
   */
  supports_seats: true,
  default_activation_policy: 'superadmin',
  /**
   * 批99-A：這條產品線開放由我方代管。**缺鍵＝false**，是本表唯一的 fail-closed 破例
   * （其餘九鍵的兜底值都是「CS 的行為」）——代管佔我方 VM 資源與維運責任，
   * 「沒設定」必須解讀成「沒開放」。後端 `services/products.OPTION_FALLBACKS` 同一顆。
   */
  supports_hosting: false,
  /**
   * 批153：開放 plugin 市集——第二個 fail-closed 鍵（理由同 `supports_hosting`：「沒設定」
   * ＝沒開放）。後端 `services/products.OPTION_FALLBACKS` 同一顆。
   */
  supports_plugin_market: false,
  /**
   * 批153：市集網頁掛在發布主機型錄的哪個產品底下（型錄的產品 key）；null＝不出網頁、JSON 照常。
   */
  market_catalog_key: null as string | null,
}
export type ProductOptions = typeof OPTION_FALLBACKS

/** 取產品選項；spec 為 null 或缺鍵／值為 null → 回兜底值。 */
export function optionOf<K extends keyof ProductOptions>(
  spec: ProductSpec | null | undefined,
  name: K,
): ProductOptions[K] {
  const v = spec?.options?.[name as string]
  return (v == null ? OPTION_FALLBACKS[name] : v) as ProductOptions[K]
}

/** 批155：內部套裝（缺鍵＝false）。 */
export function isInternalPlan(p: Pick<PlanSpec, 'internal'> | null | undefined): boolean {
  return p?.internal === true
}

/** 批155：內部模組（缺鍵＝false）。 */
export function isInternalModule(m: Pick<ModuleSpec, 'internal'> | null | undefined): boolean {
  return m?.internal === true
}

/**
 * 套裝清單的選項（批155）。
 *
 * - `internal`：要不要列出**內部套裝**。後台核發／換證／審核表單傳 `auth.isManagerOrAbove`
 *   ——內部套裝只有營運主管以上看得到、選得到（後端 403 `internal_plan_requires_manager`）。
 *   缺省＝不列（與後端 `sellable_plans` 同一條：任何投影都不列內部套裝）。
 * - `keep`：這個 key 即使是內部套裝也照列（例如現行證或申請內容本來就是它）——否則
 *   chip／下拉會一個都不亮、`<select>` 變空白，操作者看不出現在是什麼。能不能**選**它
 *   由呼叫端另外判斷（`isInternalPlan(p) && !canInternal` 時畫成停用）。
 */
export interface PlanListOptions {
  internal?: boolean
  keep?: string | null
}

function internalVisible(p: PlanSpec, opts: PlanListOptions | undefined): boolean {
  if (!isInternalPlan(p)) return true
  return opts?.internal === true || (!!opts?.keep && p.key === opts.keep)
}

/**
 * 可販售的套裝（核發／申請表單的 chip 列）。
 *
 * 與後端 `services/products.sellable_plans` **同一組判準**：只有 `kind === 'plan'`
 * 且 `is_active`——試用（`trial`）與加購（`addon`）刻意不在其中；批155 起**內部套裝**也
 * 不在其中（除非 `opts.internal`，見 `PlanListOptions`）。排序以 `sort_order`
 * 為主、`key` 為次（同序時才用得到，讓畫面順序穩定）。
 */
export function sellablePlans(spec: ProductSpec | null | undefined, opts?: PlanListOptions): PlanSpec[] {
  return (spec?.plans ?? [])
    .filter((p) => p.kind === 'plan' && p.is_active && internalVisible(p, opts))
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order || a.key.localeCompare(b.key))
}

/**
 * **我方／代理商後台**的表單可以選的套裝＝可販售的 ∪ 試用模板。
 *
 * ★ 為什麼不直接用 `sellablePlans`：那一支的不變式是「試用不出現在**客戶的**比較表裡」
 *   （試用是我方單方面給的，不是可以下單的商品）。但後台與代理商的核發／草稿表單是
 *   **發證的地方**，不是價目表——批93-C 主人裁示「trial 套裝的 `activation_policy`
 *   ＝`reseller`（代理商可直開試用）」，chip 列少了它，那條裁示在 UI 上就完全按不到。
 *
 * 試用排在最後（它不是主力商品，但要按得到）。
 * 批155：內部套裝的可見性見 `PlanListOptions`（缺省＝不列）。
 */
export function issuablePlans(spec: ProductSpec | null | undefined, opts?: PlanListOptions): PlanSpec[] {
  const trial = trialPlanOf(spec)
  const list = sellablePlans(spec, opts)
  return trial && internalVisible(trial, opts) ? [...list, trial] : list
}

/** 該產品的試用模板（`kind === 'trial'` 的第一列，取 `sort_order` 最小者）；沒有＝null。 */
export function trialPlanOf(spec: ProductSpec | null | undefined): PlanSpec | null {
  const rows = (spec?.plans ?? [])
    .filter((p) => p.kind === 'trial' && p.is_active)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
  return rows[0] ?? null
}

/** 依 key 取套裝（含 trial／addon／已停用——顯示歷史授權的套裝名要查得到）。 */
export function planByKey(
  spec: ProductSpec | null | undefined,
  key: string | null | undefined,
): PlanSpec | null {
  if (!key) return null
  return (spec?.plans ?? []).find((p) => p.key === key) ?? null
}

/**
 * 期限下拉的可選值，單位**總分鐘**（批108）——後端 `services/products.term_options_of`
 * 的前端鏡像。
 *
 * 兩個來源，新的優先：`term_options`（分鐘）／`term_day_options`（天，×1440 換算）。
 * 兩者都空就退回兜底的天數清單。**去重＋升冪**：`term_options: [43200]` 與
 * `term_day_options: [30]` 是同一個期限，下拉不該出現兩個「30 天」。
 */
export function termMinuteOptions(spec: ProductSpec | null | undefined): number[] {
  const raw = optionOf(spec, 'term_options')
  const mins = Array.isArray(raw) ? raw.map(Number).filter((n) => Number.isFinite(n) && n > 0) : []
  if (mins.length) return [...new Set(mins)].sort((a, b) => a - b)
  const days = optionOf(spec, 'term_day_options')
  const list = Array.isArray(days) ? days.map(Number).filter((n) => Number.isFinite(n) && n > 0) : []
  const fallback = list.length ? list : OPTION_FALLBACKS.term_day_options
  return [...new Set(fallback.map((d: number) => d * MINUTES_PER_DAY))].sort(
    (a, b) => (a as number) - (b as number),
  ) as number[]
}

/**
 * 期限**沒有任何來源可帶**（申請內容／套裝／現行證都沒給）時的預設，總分鐘。
 *
 * 順序＝後端 `services/license_request._first_term` 的最後一段：產品宣告的
 * `default_term_days` → 產品期限清單的第一個 → 365 天。★ 不能直接拿清單第一個：
 * CS 的清單是 30／365、預設是 365，拿第一個會讓待審核詳情顯示「30 天」而核發實簽一年
 *（批114 走查實抓）——審核者看到的不是他按下去會發生的事。
 */
export function defaultTermTotal(spec: ProductSpec | null | undefined): number {
  const def = Number(optionOf(spec, 'default_term_days')) * MINUTES_PER_DAY
  if (Number.isFinite(def) && def > 0) return def
  return termMinuteOptions(spec)[0] ?? 365 * MINUTES_PER_DAY
}

/**
 * 期限下拉的選項（**總分鐘**）＝產品的可選期限 ∪ 傳進來的值（去重、升冪）。
 *
 * ★ 為什麼需要 union：套裝可以有產品清單裡**沒有**的期限（CS 種子的試用是 90 天，
 *   而 `term_day_options` 是 `[30, 365]`）。只用產品清單的話，選了試用之後 `<select>`
 *   找不到匹配的 `<option>` → **整格空白**，而送出去的值其實是 90 天。
 *   「畫面空白但送得出東西」是最糟的一種：操作者以為漏填，實際上內容是對的。
 */
export function termOptionsWith(
  spec: ProductSpec | null | undefined,
  ...extra: (number | null | undefined)[]
): number[] {
  const set = new Set(termMinuteOptions(spec))
  for (const m of extra) {
    const n = Number(m)
    if (m != null && Number.isFinite(n) && n > 0) set.add(n)
  }
  return [...set].sort((a, b) => a - b)
}

/** 期限（總分鐘）→ 中文。43200 → 「30 天」、120 → 「2 小時」、30 → 「30 分鐘」。 */
export function termLabel(minutes: number): string {
  return formatTerm(...splitMinutes(minutes))
}

/**
 * 「全模組」起手值（每個**未停用**的模組鍵都給它的 `default_scope`）。
 *
 * 批93 之前是 `Object.keys(MODULE_LABELS)` 推導，新增模組鍵時常常漏改（批81 的 `api`
 * 就漏過）。現在鍵集合來自該產品的憑證設計，不可能再漏；`deprecated` 的鍵不帶
 * （已停售的模組不該在新證上憑空長出來）。
 * 批155：**內部模組也不帶**——「全模組」是可販售模組全開；內部模組要明確勾選
 * （而且只有營運主管以上能勾）。
 */
export function defaultModulesOf(spec: ProductSpec | null | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  for (const m of spec?.modules ?? []) {
    if (m.deprecated || isInternalModule(m)) continue
    out[m.key] = m.default_scope || '*'
  }
  return out
}

/**
 * 核發政策（`activation_policy`）的中文名——語意＝**可免審直接核發的最低身分**。
 *
 * 批97 Bug 4：值一律全中文（角色名取自 `lib/roles.ts::ROLE_LABELS`，這裡刻意寫成
 * 完整句子而非拼接，字面比較好讀）；`lib/roles.spec.ts`／`products.spec.ts` 有看門狗
 * 釘住「不得出現英文角色代號」。
 */
export const ACTIVATION_POLICY_LABELS: Record<string, string> = {
  superadmin: '須超級管理者核發',
  // 批110：付費套裝的新基準線（`0027` 把既有的 superadmin 一律校正成這一級）。
  manager: '營運主管可免審核發',
  admin: '管理員可免審核發',
  reseller: '代理商與管理員可免審核發',
}
export function activationPolicyLabel(policy: string | null | undefined): string {
  return policy ? (ACTIVATION_POLICY_LABELS[policy] ?? policy) : '—'
}

/** 套裝種類的中文名。 */
export const PLAN_KIND_LABELS: Record<string, string> = {
  plan: '套裝',
  trial: '試用',
  addon: '加購',
}
export function planKindLabel(kind: string): string {
  return PLAN_KIND_LABELS[kind] ?? kind
}

/** 產品狀態的中文名（`deprecated`＝停售，核發下拉不再出現但歷史授權照樣查得到）。 */
export const PRODUCT_STATUS_LABELS: Record<string, string> = {
  active: '販售中',
  deprecated: '已停售',
}
export function productStatusLabel(status: string): string {
  return PRODUCT_STATUS_LABELS[status] ?? status
}
